@if (showsReview()) {
  <div
    class="grid gap-6 md:grid-cols-2 py-2"
    [attr.data-testid]="'suggestion-review-' + attribute().directusId"
  >
    @if (suggestion(); as suggestion) {
      <section class="flex flex-col gap-3">
        <div class="flex items-center gap-2 flex-wrap">
          <h4 class="text-base font-semibold m-0">
            {{ 'provider.templates.suggestions.review.aiTitle' | transloco }}
          </h4>
          @if (confidenceKey(); as confidenceKey) {
            <p-tag [value]="confidenceKey | transloco" [severity]="confidenceSeverity()" />
          }
          @if (suggestion.productVersion) {
            <span class="text-xs text-gray-500">
              {{
                'provider.templates.suggestions.review.productVersion'
                  | transloco: {version: suggestion.productVersion}
              }}
            </span>
          }
        </div>

        <div>
          <span class="text-xs text-gray-500">
            {{ 'provider.templates.suggestions.review.suggestedValue' | transloco }}
          </span>
          <app-suggestion-answer-display
            [display]="suggestedAnswer()"
            [testId]="'suggestion-suggested-value-' + attribute().directusId"
          />
        </div>

        @if (suggestion.reasoning) {
          <div>
            <span class="text-xs text-gray-500">
              {{ 'provider.templates.suggestions.review.reasoning' | transloco }}
            </span>
            <p
              class="text-sm mt-1"
              [attr.data-testid]="'suggestion-reasoning-' + attribute().directusId"
            >
              {{ suggestion.reasoning }}
            </p>
          </div>
        }

        <div>
          <span class="text-xs text-gray-500">
            {{ 'provider.templates.suggestions.review.evidence.title' | transloco }}
          </span>
          @if (hasEvidence()) {
            <ul class="flex flex-col gap-3 mt-1 list-none p-0 m-0">
              @for (row of evidence(); track $index) {
                <li
                  class="flex flex-col gap-1"
                  [attr.data-testid]="'suggestion-evidence-' + attribute().directusId"
                >
                  <div class="flex items-center gap-2 flex-wrap text-sm">
                    @if (row.openable) {
                      <p-button
                        [label]="row.evidence.fileName"
                        icon="pi pi-file"
                        [link]="true"
                        (onClick)="openEvidence(row)"
                        [attr.data-testid]="'suggestion-evidence-open-' + attribute().directusId"
                      />
                    } @else {
                      <span class="font-medium">{{ row.evidence.fileName }}</span>
                      <span class="text-xs text-gray-500">
                        {{
                          'provider.templates.suggestions.review.evidence.documentRemoved'
                            | transloco
                        }}
                      </span>
                    }
                    <span class="text-xs text-gray-500">
                      {{
                        'provider.templates.suggestions.review.productVersion'
                          | transloco: {version: row.evidence.productVersion}
                      }}
                    </span>
                    <span class="text-xs text-gray-500">
                      {{ row.locationKey | transloco: {location: row.evidence.location} }}
                    </span>
                    <p-tag [value]="row.gradeKey | transloco" [severity]="row.gradeSeverity" />
                  </div>
                  <blockquote class="border-l-4 border-gray-300 pl-3 text-sm italic m-0">
                    {{ row.evidence.excerpt }}
                  </blockquote>
                </li>
              }
            </ul>
          } @else {
            <p class="text-sm mt-1 text-gray-500">
              {{ 'provider.templates.suggestions.review.evidence.none' | transloco }}
            </p>
          }
        </div>
      </section>
    } @else {
      <section class="flex flex-col gap-3">
        <h4 class="text-base font-semibold m-0">
          {{ 'provider.templates.suggestions.review.aiTitle' | transloco }}
        </h4>
        <p class="text-sm text-gray-500">
          {{ 'provider.templates.suggestions.clarification.noSuggestion' | transloco }}
        </p>
      </section>
    }

    <section class="flex flex-col gap-3">
      <h4 class="text-base font-semibold m-0">
        {{ 'provider.templates.suggestions.review.providerTitle' | transloco }}
      </h4>

      <div>
        <span class="text-xs text-gray-500">
          {{ 'provider.templates.suggestions.review.currentValue' | transloco }}
        </span>
        <app-suggestion-answer-display
          [display]="currentAnswer()"
          [testId]="'suggestion-current-value-' + attribute().directusId"
        />
      </div>

      @if (attribute().comment) {
        <div>
          <span class="text-xs text-gray-500">
            {{ 'provider.templates.suggestions.review.comment' | transloco }}
          </span>
          <p class="text-sm mt-1">{{ attribute().comment }}</p>
        </div>
      }

      <div>
        <span class="text-xs text-gray-500">
          {{ 'provider.templates.suggestions.review.lastChange.label' | transloco }}
        </span>
        @if (latestChange(); as change) {
          <p
            class="text-sm mt-1"
            [attr.data-testid]="'suggestion-last-change-' + attribute().directusId"
          >
            {{
              'provider.templates.suggestions.review.lastChange.value'
                | transloco
                  : {name: change.changedBy ?? '–', date: (change.changedAt | date: 'short')}
            }}
            @if (latestChangeSourceKey(); as sourceKey) {
              <span class="text-gray-500">({{ sourceKey | transloco }})</span>
            }
          </p>
        } @else if (historyLoading()) {
          <p class="text-sm mt-1 text-gray-500">{{ 'common.loading' | transloco }}</p>
        } @else {
          <p class="text-sm mt-1 text-gray-500">
            {{ 'provider.templates.suggestions.review.lastChange.none' | transloco }}
          </p>
        }
      </div>

      @if (reviewRequiredVersion(); as version) {
        <p-message severity="warn" variant="simple" size="small">
          {{
            'provider.templates.suggestions.review.reviewRequiredHint'
              | transloco: {version: version}
          }}
        </p-message>
      }

      @if (clarification(); as clarification) {
        <p-message
          severity="warn"
          variant="simple"
          size="small"
          [attr.data-testid]="'suggestion-clarification-note-' + attribute().directusId"
        >
          <span class="font-medium">
            {{ 'provider.templates.suggestions.clarification.title' | transloco }}
          </span>
          @if (clarification.note) {
            <span>: {{ clarification.note }}</span>
          }
          @if (clarification.markedBy) {
            <span class="text-xs">
              ({{
                'provider.templates.suggestions.clarification.markedBy'
                  | transloco
                    : {
                        name: clarification.markedBy,
                        date: (clarification.markedAt | date: 'short'),
                      }
              }})
            </span>
          }
        </p-message>
      }
    </section>

    @if (showsPrimaryActions()) {
      <div class="md:col-span-2 flex flex-wrap gap-3" data-testid="suggestion-actions">
        <p-button
          [label]="'provider.templates.suggestions.actions.accept' | transloco"
          icon="pi pi-check"
          severity="primary"
          [disabled]="busy()"
          [loading]="busy()"
          (onClick)="accept()"
          [attr.data-testid]="'suggestion-accept-' + attribute().directusId"
        />
        <p-button
          [label]="'provider.templates.suggestions.actions.override' | transloco"
          icon="pi pi-pencil"
          severity="secondary"
          [outlined]="true"
          [disabled]="busy()"
          (onClick)="startOverride()"
          [attr.data-testid]="'suggestion-override-' + attribute().directusId"
        />
        <p-button
          [label]="'provider.templates.suggestions.actions.reject' | transloco"
          icon="pi pi-times"
          severity="danger"
          [text]="true"
          [disabled]="busy()"
          (onClick)="reject()"
          [attr.data-testid]="'suggestion-reject-' + attribute().directusId"
        />
      </div>
    }

    @if (showsOverrideEditor()) {
      <div
        class="md:col-span-2 flex flex-col gap-3 border-t border-gray-200 pt-3"
        [attr.data-testid]="'suggestion-override-editor-' + attribute().directusId"
      >
        <span class="text-xs text-gray-500">
          {{ 'provider.templates.suggestions.actions.overrideHint' | transloco }}
        </span>
        <app-attribute-fulfillment-input
          [attribute]="draftAttribute()"
          [twoStage]="twoStage()"
          [disabled]="busy()"
          [testIdKey]="'suggestion-override-' + attribute().directusId"
          (valueChange)="onDraftChange($event)"
        />
        <div class="w-96">
          <app-model-field-text
            [id]="'suggestion-override-reason-' + attribute().directusId"
            label="provider.templates.suggestions.actions.reason"
            [smallLabel]="true"
            [(value)]="reason"
          />
        </div>
        <div class="flex flex-wrap gap-3">
          @if (canConfirmOverride()) {
            <p-button
              [label]="'provider.templates.suggestions.actions.overrideConfirm' | transloco"
              icon="pi pi-check"
              severity="primary"
              [disabled]="busy()"
              [loading]="busy()"
              (onClick)="confirmOverride()"
              [attr.data-testid]="'suggestion-override-confirm-' + attribute().directusId"
            />
          }
          <p-button
            [label]="'common.cancel' | transloco"
            severity="secondary"
            [text]="true"
            (onClick)="cancelOverride()"
            [attr.data-testid]="'suggestion-override-cancel-' + attribute().directusId"
          />
        </div>
      </div>
    }

    @if (showsMarkClarification()) {
      <div class="md:col-span-2 flex flex-wrap gap-3">
        <p-button
          [label]="'provider.templates.suggestions.clarification.mark' | transloco"
          icon="pi pi-question-circle"
          severity="secondary"
          [text]="true"
          [disabled]="clarificationBusy()"
          (onClick)="startClarification()"
          [attr.data-testid]="'suggestion-clarification-mark-' + attribute().directusId"
        />
      </div>
    }

    @if (showsClarificationEditor()) {
      <div
        class="md:col-span-2 flex flex-col gap-3 border-t border-gray-200 pt-3"
        [attr.data-testid]="'suggestion-clarification-editor-' + attribute().directusId"
      >
        <div class="w-96">
          <app-model-field-text
            [id]="'suggestion-clarification-note-' + attribute().directusId"
            label="provider.templates.suggestions.clarification.note"
            [smallLabel]="true"
            [(value)]="clarificationNote"
          />
        </div>
        <div class="flex flex-wrap gap-3">
          <p-button
            [label]="'provider.templates.suggestions.clarification.confirm' | transloco"
            icon="pi pi-question-circle"
            severity="primary"
            [disabled]="clarificationBusy()"
            [loading]="clarificationBusy()"
            (onClick)="confirmClarification()"
            [attr.data-testid]="'suggestion-clarification-confirm-' + attribute().directusId"
          />
          <p-button
            [label]="'common.cancel' | transloco"
            severity="secondary"
            [text]="true"
            (onClick)="cancelClarification()"
            [attr.data-testid]="'suggestion-clarification-cancel-' + attribute().directusId"
          />
        </div>
      </div>
    }

    @if (showsClearClarification()) {
      <div class="md:col-span-2 flex flex-wrap gap-3">
        <p-button
          [label]="'provider.templates.suggestions.clarification.unmark' | transloco"
          icon="pi pi-check-circle"
          severity="secondary"
          [outlined]="true"
          [disabled]="clarificationBusy()"
          [loading]="clarificationBusy()"
          (onClick)="clearClarification()"
          [attr.data-testid]="'suggestion-clarification-clear-' + attribute().directusId"
        />
      </div>
    }
  </div>
}
